import React, { useRef, useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  SxProps,
  Chip,
  Stack,
  Box
} from '@mui/material';
import ReorderIcon from '@mui/icons-material/Reorder';
import VerifiedIcon from '@mui/icons-material/Verified';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
  TimetableItem,
  TimetableDetails,
  selectTimetableDetailsByUuid,
  selectTimetableDetailsForItems,
  selectMonitorDetailsForItems,
  selectAreAllHeatsCompleted,
  selectIsResultsPresented,
  selectHasResults,
  selectShowsFinalResults,
  toggleManualTTItemCompletion
} from '../../../redux/timetableSlice';
import DateTimeFormatter from '../../../helpers/DateTimeFormatter';
import type { Theme } from '@mui/material/styles';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { selectActiveTasksForHall } from '../../../redux/workSlice';
import { selectMonitorFontFamily } from '../../../redux/settingsSlice';
import {
  isResultsAvailable,
  isResultsItem,
  resultsCategoriesOf
} from '../../../helpers/resultsPresentation';

/** Where one category of a results presentation stands. */
type ResultsCategoryState = 'presented' | 'available' | 'unavailable';

/** How far a round chaired by somebody else is faded on the chairperson screen. */
const OTHER_CHAIRPERSON_OPACITY = 0.45;

interface TimetableTableListProps {
  timetable: TimetableItem[];
  selectedItem: TimetableItem | null;
  handleRowClick: ((item: TimetableItem) => void) | null;
  t: (key: string) => string;
  currentAdjudicator?: {
    letter: string;
    name: string;
    uuid: string;
  };
  mode?: 'timetable' | 'monitor';
  hideResultsStatus?: boolean;
  /** Hide the Dances column entirely */
  hideDancesColumn?: boolean;
  /** Prepend round content to the Category (text) column */
  prependRoundToCategory?: boolean;
  /**
   * The chairperson signed in on this device. Rounds whose category is
   * assigned to a different chairperson are dimmed; everything else, including
   * categories nobody chairs, renders as usual. Not `currentAdjudicator`: that
   * one also hides the status icons and the results highlight, which a
   * chairperson needs on every row.
   */
  chairpersonUuid?: string;
}

const TimetableTableList: React.FC<TimetableTableListProps> = ({
  timetable,
  selectedItem,
  handleRowClick,
  t,
  currentAdjudicator,
  mode = 'timetable', // Default to 'timetable' if not provided
  hideResultsStatus = false,
  hideDancesColumn = false,
  prependRoundToCategory = false,
  chairpersonUuid
}) => {
  const dispatch = useAppDispatch();
  const activeTasks = useAppSelector(selectActiveTasksForHall);

  // Long press state
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(
    null
  );
  const [isTouchInteraction, setIsTouchInteraction] = useState(false);

  // Select details for all timetable items
  const detailsArray = useAppSelector((state) =>
    mode === 'monitor'
      ? selectMonitorDetailsForItems(state, timetable)
      : // Use the original selector for 'timetable' mode
        selectTimetableDetailsForItems(state, timetable)
  );
  // Build detailsMap from array
  const detailsMap: Record<string, TimetableDetails | undefined> = {};
  detailsArray.forEach((detail) => {
    if (detail) detailsMap[detail.ttUuid] = detail;
  });

  // Compute the set of ttUuids that belong to any active task's joined group,
  // plus the first such row in display order (the "now" row to scroll to).
  // Recomputed only when timetable or activeTasks reference changes; the
  // scroll effect below depends on the stable ttUuid string instead of the
  // arrays themselves so it does not fire on unrelated re-renders.
  const { nowRowSet, nowScrollTtUuid } = useMemo(() => {
    if (activeTasks.length === 0) {
      return {
        nowRowSet: new Set<string>(),
        nowScrollTtUuid: null as string | null
      };
    }
    const taskParents = new Set<string>();
    for (const task of activeTasks) {
      if (!task.ttItemUuid) continue;
      const taskItem = timetable.find((t) => t.ttUuid === task.ttItemUuid);
      if (!taskItem) continue;
      taskParents.add(taskItem.isJoinedTo || taskItem.ttUuid);
    }
    const set = new Set<string>();
    let firstUuid: string | null = null;
    for (const item of timetable) {
      const parent = item.isJoinedTo || item.ttUuid;
      if (taskParents.has(parent)) {
        set.add(item.ttUuid);
        if (firstUuid === null) firstUuid = item.ttUuid;
      }
    }
    return { nowRowSet: set, nowScrollTtUuid: firstUuid };
  }, [timetable, activeTasks]);

  const isInCurrentTaskGroup = (item: TimetableItem): boolean =>
    nowRowSet.has(item.ttUuid);

  // Single ref to the "now" row; effect fires only when the target row's
  // ttUuid actually changes (not on every selector tick).
  const nowRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (nowScrollTtUuid && nowRowRef.current) {
      nowRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [nowScrollTtUuid]);

  const fontFamily = useAppSelector(selectMonitorFontFamily);
  const showStatusIndicator = !currentAdjudicator;
  const showDancesColumn = !hideDancesColumn;

  // Long press handlers
  const handleTouchStart = (item: TimetableItem, isRound: boolean) => {
    if (isRound) return; // Only for non-round items
    setIsTouchInteraction(true);
    const timer = setTimeout(() => {
      dispatch(toggleManualTTItemCompletion(item.ttUuid));
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setTimeout(() => setIsTouchInteraction(false), 50);
  };

  const handleMouseDown = (item: TimetableItem, isRound: boolean) => {
    if (isRound) return; // Only for non-round items
    const timer = setTimeout(() => {
      dispatch(toggleManualTTItemCompletion(item.ttUuid));
    }, 500);
    setLongPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Pre-compute completion status for all items to avoid calling hooks in loops
  const completedHeatsData = useAppSelector((state) => {
    const result: Record<string, boolean> = {};
    timetable.forEach((item) => {
      result[item.ttUuid] = selectAreAllHeatsCompleted(state, item.ttUuid);
    });
    return result;
  });

  // Pre-compute results status for all items
  const resultsStatusData = useAppSelector((state) => {
    const result: Record<
      string,
      { hasResults: boolean; isPresented: boolean; isFinalLike: boolean }
    > = {};
    timetable.forEach((item) => {
      result[item.ttUuid] = {
        hasResults: selectHasResults(state, item.ttUuid),
        isPresented: selectIsResultsPresented(state, item.ttUuid),
        // Finals and Show rounds both highlight results the same way.
        isFinalLike: selectShowsFinalResults(state, item.ttUuid)
      };
    });
    return result;
  });

  // Results presentations: where each announced category stands. Only screens
  // that are sent results can tell - a monitor, a DJ or a floor manager shows
  // the bare order of presentation.
  const showResultsMarkers = mode !== 'monitor' && !hideResultsStatus;
  const resultsCategoryStates = useAppSelector((state) => {
    const result: Record<string, ResultsCategoryState[]> = {};
    if (!showResultsMarkers) return result;
    timetable.forEach((item) => {
      if (!isResultsItem(item)) return;
      result[item.ttUuid] = resultsCategoriesOf(item).map((category) => {
        if (
          !isResultsAvailable(
            selectTimetableDetailsByUuid(state, category.detailsUuid)
          )
        ) {
          return 'unavailable';
        }
        return selectIsResultsPresented(state, category.detailsUuid)
          ? 'presented'
          : 'available';
      });
    });
    return result;
  });

  const formatRound = (round: string | undefined) =>
    round ? round.replace('1st', '1st r.') : '';

  const formatParticipants = (participants: string | undefined) =>
    participants ? participants.replace(/\s*->\s*/g, '→') : '';

  const formatExpectedMinutes = (
    expectedMinutes: TimetableItem['expectedMinutes']
  ) => {
    const minutes =
      typeof expectedMinutes === 'number'
        ? expectedMinutes
        : Number.parseFloat(String(expectedMinutes).replace(',', '.'));

    if (!Number.isFinite(minutes) || minutes <= 0) return '';

    return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`;
  };

  const renderStatusIndicator = (
    allHeatsCompleted: boolean,
    hasDetails: boolean,
    resultsStatus: {
      hasResults: boolean;
      isPresented: boolean;
      isFinalLike: boolean;
    },
    showResultsAvailable: boolean
  ) => {
    if (!hideResultsStatus && resultsStatus.isPresented) {
      return (
        <Tooltip title={t('results.presented')}>
          <VerifiedIcon
            sx={{
              fontSize: '1.2em',
              color: (theme) => theme.palette.success.main
            }}
          />
        </Tooltip>
      );
    }

    if (!hideResultsStatus && showResultsAvailable) {
      return (
        <Tooltip title={t('results.available')}>
          <EmojiEventsIcon
            sx={{
              fontSize: '1.2em',
              color: (theme) => theme.palette.common.black
            }}
          />
        </Tooltip>
      );
    }

    if (allHeatsCompleted) {
      return (
        <CheckBoxIcon
          sx={{
            fontSize: '1.2em',
            color: (theme) => theme.palette.success.main
          }}
        />
      );
    }

    if (hasDetails) {
      return <ReorderIcon color="inherit" sx={{ fontSize: '1.2em' }} />;
    }

    return null;
  };

  const renderTimeContent = (item: TimetableItem) => (
    <Stack
      spacing={0.5}
      direction="column"
      sx={{
        alignItems: 'center',
        fontWeight: '900',
        fontFamily
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center'
        }}
      >
        {item?.isJoinedTo ? (
          '+'
        ) : item?.delay !== undefined &&
          item.delay !== 0 &&
          item.baselineStartTime ? (
          <Stack
            direction="column"
            spacing={0}
            sx={{
              alignItems: 'center'
            }}
          >
            <span
              style={{
                textDecoration: 'line-through',
                opacity: 0.6,
                fontSize: '0.85em'
              }}
            >
              {DateTimeFormatter.formatTimestampAsTime(item.baselineStartTime)}
            </span>
            <span
              style={{
                color: item.delay > 0 ? '#d32f2f' : '#2e7d32',
                fontWeight: 700
              }}
            >
              {item.time}
            </span>
          </Stack>
        ) : (
          item?.time
        )}
        {item?.floor && item.floor !== 'one' && (
          <>
            &nbsp;
            <Chip
              label={item.floor}
              size="small"
              color={item.floor === 'A' ? 'secondary' : 'info'}
              variant="filled"
              sx={{ fontSize: '0.75em', height: '20px' }}
            />
          </>
        )}
      </Stack>
      {isInCurrentTaskGroup(item) && (
        <Chip
          label="Now!"
          color="error"
          size="small"
          sx={{ mr: 1, fontFamily }}
        />
      )}
    </Stack>
  );

  const renderHeatsIndicator = (item: TimetableItem) => {
    if ((item.heats ?? 0) <= 1) return null;

    return (
      <Tooltip title={t('heats.heats')}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            whiteSpace: 'nowrap',
            lineHeight: 1
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '1em',
              fontWeight: 800,
              opacity: 0.82
            }}
          >
            {t('heats.heats')}
          </Box>
          <Box
            component="span"
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.6,
              borderRadius: '999px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.contrastText,
              fontSize: '1.2em',
              fontWeight: 900,
              lineHeight: 1
            }}
          >
            {item.heats}
          </Box>
        </Box>
      </Tooltip>
    );
  };
  // console.log(timetable);
  return (
    <TableContainer component={Paper} sx={{ userSelect: 'none' }}>
      <Table size="small" sx={{ fontFamily, tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ fontFamily }}>
            <TableCell sx={{ fontFamily, width: '5.5em' }}>
              {t('timetable.time')}
            </TableCell>
            <TableCell sx={{ fontFamily }}>{t('timetable.category')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {timetable.map((item, index) => {
            const isCurrent =
              selectedItem !== null && item?.ttUuid === selectedItem?.ttUuid;
            const isRound = item?.round !== undefined && item?.round !== '';
            // A results presentation lists the categories it announces. Where
            // results are shown it opens like a round; elsewhere it is a line
            // of text like any other non-round item.
            const isResults = isResultsItem(item);
            const categoryStates = resultsCategoryStates[item.ttUuid] || [];
            const isSelectable = isRound || (isResults && showResultsMarkers);

            const isCurrentAdjudicator =
              currentAdjudicator &&
              item?.adjudicators &&
              item?.adjudicators?.length > 0 &&
              item?.adjudicators.includes(currentAdjudicator?.uuid);

            // Somebody is assigned to this category and it is not the person
            // holding the device. A break, a category nobody chairs, and a
            // timetable sent before marX listed chairpeople all fail the test
            // and look as they always did. Dimmed is not disabled: the row
            // still opens, so one chairperson can cover for another.
            const chairedBySomeoneElse =
              !!chairpersonUuid &&
              !!item?.chairpersons?.length &&
              !item.chairpersons.includes(chairpersonUuid);

            // Determine row style
            let rowSx: SxProps<Theme> = {
              backgroundColor: isCurrent
                ? (theme: Theme) => theme.palette.warning.dark
                : 'inherit',
              color: isCurrent
                ? (theme: Theme) =>
                    theme.palette.getContrastText(theme.palette.warning.main)
                : 'inherit',
              cursor: isSelectable ? 'pointer' : 'default'
            };
            let cellSx: SxProps<Theme> = {
              // fontWeight: isCurrent ? 900 : 'normal',
            };
            if (currentAdjudicator && currentAdjudicator.uuid !== '') {
              if (isCurrentAdjudicator) {
                rowSx = {
                  ...rowSx,
                  backgroundColor: (theme: Theme) => theme.palette.warning.dark,
                  color: (theme: Theme) =>
                    theme.palette.getContrastText(theme.palette.secondary.main)
                };
              } else {
                cellSx = {
                  color: (theme: Theme) => theme.palette.text.disabled
                };
              }
            }

            // Use detailsMap instead of selector
            const hasDetails = !!detailsMap[item.ttUuid];
            // A presentation counts as having results once any category can
            // be announced, and as presented once every one of them has been.
            const resultsStatus = isResults
              ? {
                  hasResults: categoryStates.some(
                    (state) => state === 'available'
                  ),
                  isPresented:
                    categoryStates.length > 0 &&
                    categoryStates.every((state) => state === 'presented'),
                  isFinalLike: true
                }
              : resultsStatusData[item.ttUuid] || {
                  hasResults: false,
                  isPresented: false,
                  isFinalLike: false
                };

            // In monitor mode, if results are available, treat as all heats completed
            const allHeatsCompleted =
              mode === 'monitor' && resultsStatus.hasResults
                ? true
                : completedHeatsData[item.ttUuid] || false;

            // Determine if we should highlight for results available (final round with results but not yet presented)
            // Only show in timetable mode, not in monitor mode, and not when results status is hidden (DJ/Floor Manager)
            const showResultsAvailable =
              mode !== 'monitor' &&
              !hideResultsStatus &&
              resultsStatus.isFinalLike &&
              resultsStatus.hasResults &&
              !resultsStatus.isPresented;

            // Override row background if results are available (only in timetable mode)
            if (showResultsAvailable && !isCurrent && !currentAdjudicator) {
              rowSx = {
                ...rowSx,
                backgroundColor: (theme: Theme) => theme.palette.info.light,
                color: (theme: Theme) =>
                  theme.palette.getContrastText(theme.palette.info.light)
              };
            }

            const statusIndicator = renderStatusIndicator(
              allHeatsCompleted,
              hasDetails,
              resultsStatus,
              showResultsAvailable
            );
            const formattedRound = formatRound(item.round);
            const formattedParticipants = formatParticipants(item.participants);
            const formattedExpectedMinutes = formatExpectedMinutes(
              item.expectedMinutes
            );
            const tableRowSx = {
              ...rowSx,
              fontFamily,
              // The whole row fades - text, chips, colour bar and icons alike.
              // Not while it is selected: what is open below must stay legible.
              ...(chairedBySomeoneElse && !isCurrent
                ? { opacity: OTHER_CHAIRPERSON_OPACITY }
                : {}),
              borderLeft: item?.categoryColor
                ? `5px solid ${item.categoryColor}`
                : 'none',
              '& td:first-of-type': {
                paddingLeft: item?.categoryColor ? '12px' : '16px'
              }
            };

            return (
              <TableRow
                key={`timetable-row-${index}`}
                ref={(el) => {
                  if (item.ttUuid === nowScrollTtUuid) {
                    nowRowRef.current = el;
                  }
                }}
                sx={tableRowSx}
                onClick={() => {
                  if (isTouchInteraction) return;
                  if (isSelectable && handleRowClick !== null)
                    handleRowClick(item);
                }}
                onTouchStart={() => handleTouchStart(item, isSelectable)}
                onTouchEnd={handleTouchEnd}
                onMouseDown={() => handleMouseDown(item, isSelectable)}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <TableCell
                  sx={{
                    ...cellSx,
                    fontFamily,
                    verticalAlign: 'top',
                    width: '5.5em',
                    py: 1
                  }}
                >
                  <Stack
                    spacing={0.5}
                    sx={{
                      alignItems: 'center',
                      fontFamily
                    }}
                  >
                    {renderTimeContent(item)}
                    {showStatusIndicator && statusIndicator && (
                      <Box sx={{ display: 'flex', lineHeight: 0 }}>
                        {statusIndicator}
                      </Box>
                    )}
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    fontFamily,
                    minWidth: 0,
                    py: isRound || isResults ? 0.75 : 1,
                    pr: 1.25
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      columnGap: 1,
                      rowGap: isRound || isResults ? 0.35 : 0,
                      alignItems: 'start',
                      minWidth: 0
                    }}
                  >
                    <Box
                      sx={{
                        gridColumn: 1,
                        gridRow: 1,
                        minWidth: 0,
                        fontWeight: isRound || isResults ? 700 : 600,
                        lineHeight: 1.25,
                        overflowWrap: 'anywhere',
                        fontSize: isRound || isResults ? '1.1em' : '1em'
                      }}
                    >
                      {prependRoundToCategory && isRound && formattedRound
                        ? `${formattedRound} · ${item.text}`
                        : item?.text}
                      {isRound && (item.heats ?? 0) > 1 && (
                        <Box
                          component="span"
                          sx={{
                            justifySelf: 'end',
                            alignSelf: 'start',
                            pt: 0.05,
                            pl: 4
                          }}
                        >
                          {renderHeatsIndicator(item)}
                        </Box>
                      )}
                    </Box>
                    {formattedExpectedMinutes && (
                      <Box
                        component="span"
                        sx={{
                          gridColumn: 3,
                          gridRow: 1,
                          justifySelf: 'end',
                          pt: 0.15,
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                          textAlign: 'right'
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            justifySelf: 'end',
                            pt: 0.15,
                            fontSize: '0.9em',
                            fontWeight: 800,
                            opacity: 0.82,
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                            textAlign: 'right'
                          }}
                        >
                          {formattedExpectedMinutes}
                        </Box>
                      </Box>
                    )}
                    {isRound && (
                      <>
                        <Box
                          sx={{
                            gridColumn: 1,
                            gridRow: 2,
                            display: 'flex',
                            flexWrap: 'wrap',
                            columnGap: 1.25,
                            rowGap: 0.25,
                            alignItems: 'baseline',
                            minWidth: 0,
                            fontSize: '1em',
                            lineHeight: 1.25
                          }}
                        >
                          {formattedRound && !prependRoundToCategory && (
                            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                              <Box
                                component="span"
                                sx={{ opacity: 0.72, fontWeight: 600 }}
                              >
                                {t('timetable.round')}:
                              </Box>{' '}
                              <Box component="span" sx={{ fontWeight: 800 }}>
                                {formattedRound}
                              </Box>
                            </Box>
                          )}
                          {formattedParticipants && (
                            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                              <Box
                                component="span"
                                sx={{ opacity: 0.72, fontWeight: 600 }}
                              >
                                {t('timetable.participants')}:
                              </Box>{' '}
                              <Box component="span" sx={{ fontWeight: 900 }}>
                                {formattedParticipants}
                              </Box>
                            </Box>
                          )}
                        </Box>
                        {/* {(item.heats ?? 0) > 1 && (
                          <Box
                            sx={{
                              gridColumn: 2,
                              gridRow: 1,
                              justifySelf: 'end',
                              alignSelf: 'start',
                              pt: 0.05
                            }}
                          >
                            {renderHeatsIndicator(item)}
                          </Box>
                        )} */}
                        {item.dances && showDancesColumn && (
                          <Box
                            sx={{
                              // gridColumn: '1 / -1',
                              gridColumn: 3,
                              gridRow: 2,
                              display: 'flex',
                              columnGap: 0.5,
                              minWidth: 0,
                              alignItems: 'baseline',
                              fontSize: '1em',
                              lineHeight: 1.25
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                opacity: 0.72,
                                fontWeight: 600,
                                flex: '0 0 auto'
                              }}
                            >
                              {t('timetable.dances')}:
                            </Box>
                            <Box
                              component="span"
                              sx={{ fontWeight: 650, overflowWrap: 'anywhere' }}
                            >
                              {item.dances}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                    {isResults && (
                      <Box
                        sx={{
                          gridColumn: '1 / 4',
                          gridRow: 2,
                          display: 'flex',
                          flexWrap: 'wrap',
                          columnGap: 1.25,
                          rowGap: 0.25,
                          alignItems: 'baseline',
                          minWidth: 0,
                          fontSize: '1em',
                          lineHeight: 1.25
                        }}
                      >
                        {resultsCategoriesOf(item).map((category, position) => {
                          const state = showResultsMarkers
                            ? categoryStates[position]
                            : undefined;
                          return (
                            <Box
                              component="span"
                              key={category.categoryUuid}
                              sx={{
                                overflowWrap: 'anywhere',
                                opacity: state === 'unavailable' ? 0.6 : 1
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ opacity: 0.72, fontWeight: 600 }}
                              >
                                {position + 1}.
                              </Box>{' '}
                              <Box component="span" sx={{ fontWeight: 800 }}>
                                {category.name}
                              </Box>
                              {state === 'presented' && (
                                <VerifiedIcon
                                  sx={{
                                    fontSize: '1em',
                                    ml: 0.25,
                                    verticalAlign: 'text-bottom'
                                  }}
                                />
                              )}
                              {state === 'unavailable' && (
                                <HourglassEmptyIcon
                                  sx={{
                                    fontSize: '1em',
                                    ml: 0.25,
                                    verticalAlign: 'text-bottom'
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TimetableTableList;
